import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon/Icon";
import { PillGroup, PillGroupItems } from "@ui/components/PillGroup";
import { SortMenu, type SortMenuProps } from "@ui/components/SortMenu";
import { useLocale } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import { WhenPopover, type WhenPopoverProps } from "./popovers/WhenPopover";
import { WherePopover, type WherePopoverProps } from "./popovers/WherePopover";
import { WhoPopover, type WhoPopoverProps } from "./popovers/WhoPopover";
import { SearchBarFilters } from "./SearchBarFilters";
import { SearchBarSection } from "./SearchBarSection";
import {
  type SearchMember,
  WHEN_DURATION_I18N_KEYS,
  type WhenDatePart,
  type WhereSelection,
  type WhoSelection,
} from "./types";

export interface SearchBarRootProps {
  children: ReactNode;
  className?: string;
}

// ---------- Named section components ----------

export interface SearchBarWhereProps extends WherePopoverProps {}

function whereDescription(value: WhereSelection | null): string {
  if (!value) return t("components.search_bar.where_description");
  return value.name;
}

function SearchBarWhere(props: SearchBarWhereProps) {
  return (
    <SearchBarSection
      clearAriaLabel={t("components.search_bar.where_clear")}
      description={whereDescription(props.value)}
      heading={t("components.search_bar.where_heading")}
      icon={<Icon name="location" type="sticker" />}
      isActive={!!props.value}
      onClear={() => props.onChange(null)}
      popoverAriaLabel={t("components.search_bar.where_title")}
      popoverContent={<WherePopover {...props} />}
    />
  );
}

export interface SearchBarWhenProps extends WhenPopoverProps {}

function formatDatePart(part: WhenDatePart, locale: string): string {
  if (part.kind === "month") {
    return new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format(
      new Date(part.year, part.month - 1, 1),
    );
  }
  if (part.kind === "date") {
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(
      part.value.toDate ? part.value.toDate("UTC") : new Date(part.value.year, part.value.month - 1, part.value.day),
    );
  }
  return t(WHEN_DURATION_I18N_KEYS[part.preset]);
}

function whenDescription(value: { start: WhenDatePart | null; end: WhenDatePart | null }, locale: string): string {
  if (!value.start && !value.end) return t("components.search_bar.when_description");
  const startLabel = value.start ? formatDatePart(value.start, locale) : "…";
  const endLabel = value.end ? formatDatePart(value.end, locale) : "…";
  return `${startLabel} — ${endLabel}`;
}

function SearchBarWhen(props: SearchBarWhenProps) {
  const { locale } = useLocale();
  return (
    <SearchBarSection
      clearAriaLabel={t("components.search_bar.when_clear")}
      description={whenDescription(props.value, locale)}
      heading={t("components.search_bar.when_heading")}
      icon={<Icon name="date" type="sticker" />}
      isActive={!!(props.value.start || props.value.end)}
      onClear={() => props.onChange({ start: null, end: null })}
      popoverAriaLabel={t("components.search_bar.when_title")}
      popoverContent={<WhenPopover {...props} />}
    />
  );
}

export interface SearchBarWhoProps extends WhoPopoverProps {}

// Past two, names stop fitting the trigger and truncate to nothing readable —
// a count says more in the same space than "Mia Travily, Maxim…".
const WHO_NAME_LIMIT = 2;

/**
 * One-line summary of the Who selection, for the desktop trigger and the mobile
 * accordion's collapsed row — the same sentence in both, so a picked household
 * reads the same wherever the section is closed.
 */
export function whoSummary(value: WhoSelection, members: SearchMember[]): string | null {
  if (value.memberIds.length === 0) return null;
  const nameById = new Map(members.map((member) => [member.id, member.name || member.initial]));
  const names = value.memberIds.map((id) => nameById.get(id)).filter((name): name is string => !!name);
  if (names.length === 0) return null;
  if (names.length > WHO_NAME_LIMIT) return t("components.search_bar.who_count", { count: names.length });
  return names.join(", ");
}

function SearchBarWho(props: SearchBarWhoProps) {
  return (
    <SearchBarSection
      clearAriaLabel={t("components.search_bar.who_clear")}
      description={whoSummary(props.value, props.members) ?? t("components.search_bar.who_description")}
      heading={t("components.search_bar.who_heading")}
      icon={<Icon name="user" type="sticker" />}
      isActive={props.value.memberIds.length > 0}
      onClear={() => props.onChange({ memberIds: [] })}
      popoverAriaLabel={t("components.search_bar.who_title")}
      popoverContent={<WhoPopover {...props} />}
    />
  );
}

// ---------- Public compound API ----------

export interface SearchBarSectionsProps {
  children: ReactNode;
  className?: string;
}

/** Layout slot for the section row. Sections render here as siblings with gap-4. */
function SearchBarSectionsSlot({ children, className }: SearchBarSectionsProps) {
  return <PillGroupItems className={className}>{children}</PillGroupItems>;
}

/**
 * Compound wide-viewport search bar. Use `SearchBar.Sections` to wrap the
 * section triggers and place `SearchBar.Filters` as a trailing sibling:
 *
 * ```tsx
 * <SearchBar>
 *   <SearchBar.Sections>
 *     <SearchBar.Where ... />
 *     <SearchBar.When ... />
 *     <SearchBar.Who ... />
 *   </SearchBar.Sections>
 *   <SearchBar.Filters ... />
 * </SearchBar>
 * ```
 */
function SearchBarBase({ children, className }: SearchBarRootProps) {
  return (
    <PillGroup aria-label={t("components.search_bar.aria_label")} className={className} role="search">
      {children}
    </PillGroup>
  );
}

/** Sort control for the SearchBar trailing row — SortMenu with the pill trigger matching `SearchBar.Filters`. */
function SearchBarSort(props: Omit<SortMenuProps, "variant">) {
  return <SortMenu {...props} variant="searchbar-pill" />;
}

export const SearchBar = Object.assign(SearchBarBase, {
  Section: SearchBarSection,
  Sections: SearchBarSectionsSlot,
  Where: SearchBarWhere,
  When: SearchBarWhen,
  Who: SearchBarWho,
  Filters: SearchBarFilters,
  Sort: SearchBarSort,
});
